import { type ColumnNode } from "primevue/column"
import { type Component, type MaybeRef } from "vue"

import {
  type ContactPerson,
  type Address,
  type Commune,
  type Employee,
  type ProjectCustomer,
  type YouthWelfareOffice,
  type School,
  type ProjectOrganization,
  type ProjectInstitution,
  type Maybe,
  type Organization,
  type Institution,
  AppointmentType,
  type Holiday,
  type Bank,
  type ResourceType,
  type Operation,
  type FunctionalRole,
  type ContractStateInput,
  type AccessLevel,
  type Custodian,
} from "@/graphql/types"
import { type SearchResultItem, type SearchType } from "@/graphql/search_types"
import { i18n } from "@/i18n"

import { type FilterOption, type FilterType } from "./registerFilters"
import { type StrictEntityId } from "./utils/entity"

/**
 * ISO 8601 calendar date without timezone. Format: %Y-%m-%d
 *
 * @example
 * ```
 * "1994-11-13"
 * "2000-02-24"
 * ```
 */
export type NaiveDate = string

type Primitive = null | undefined | string | number | boolean | symbol | bigint

type Key = Extract<PropertyKey, string | number>

type Join<K, P> = K extends Key
  ? P extends Key
    ? `${K}${"" extends P ? "" : "."}${P}`
    : never
  : never

type Keys<T> = Extract<keyof T, Key>

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, ...0[]]

//export type Depth = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

export type DefaultDepth = 6 //4

export type GqlLeaves<T, D extends number = DefaultDepth> = [D] extends [never]
  ? never
  : [T] extends [Primitive]
    ? ""
    : T extends Array<infer A>
      ? [A] extends [Primitive]
        ? ""
        : A extends object
          ? { [K in Keys<A>]-?: Join<K, GqlLeaves<A[K], Prev[D]>> }[Keys<A>]
          : ""
      : T extends object
        ? { [K in Keys<T>]-?: Join<K, GqlLeaves<T[K], Prev[D]>> }[Keys<T>]
        : ""

export type Mock = {
  fill: () => void
  disabled?: MaybeRef<boolean>
}

export type TableConfig<TData, TMappedData = TData> = {
  columnDefs: Column<TData, TMappedData>[]
  filterableColumnDefs?: FilterableColumn<TData, TMappedData>[]
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  baseActions?: Function
}

export type Option = {
  name: string
  code: string | number
}
export type GroupOptionsItems = {
  label: string
  value: string | string[]
}
export type GroupedOptions = {
  label: string
  code: string
  items: GroupOptionsItems[]
}

/**
 * Event fired when a row in a `DataTable` component is clicked
 */
export type RowEvent<T> = {
  originalEvent: Event
  data: T
  index?: number
}

export type Suggestion = {
  id: number | string
  label: string
  searchname?: string
}

export type SuggestionGroup = {
  label: string
  items: Suggestion[]
}

export type BasePresetColumn<T extends string> = {
  key: string // if `fields` is not provided, this will double as a entity field key added to the query
  fields?: T[] // if an explicitly empty array is provided, no field key is added to the query
  header: string
  active?: boolean | (() => boolean)
  requiredPermissions?: Permission[]
}

export type Column<TData, TMappedData = TData> = BasePresetColumn<GqlLeaves<TData>> & {
  icon?: string
  width?: string
  class?: string
  format?: (data: TMappedData) => string | null | undefined | false // if not provided, `key` will be used to get the value from the mapped entity
}

export type FilterableColumn<TData, TMappedData = TData> = BasePresetColumn<
  GqlLeaves<TData | TMappedData>
> & {
  dataKey: GqlLeaves<TMappedData>
  filterType: FilterType
  matchModes?: FilterOption[]
  component?: Component
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  options?: any[] | readonly any[]
  disableSort?: boolean
  prioritizedKeys?: string[]
}

export enum LinkableResourceType {
  Address = "address",
  FoodType = "food_type",
  Commune = "commune",
  YouthWelfareOffice = "youth_welfare_office",
  School = "school",
  ServiceType = "service_type",
  Qualification = "qualification",
  Category = "category",
}

export type UserAccessLevel = AccessLevel | "Administration"

export type Permission = "administration" | `${ResourceType}:${Operation}` | FunctionalRole

export type SearchResultItemExtended = SearchResultItem & {
  ty: SearchType
}

export type RemoveMutationResult<T extends string> = {
  [K in T]: number
}

export type AddressableEntity =
  | Commune
  | YouthWelfareOffice
  | Custodian
  | School
  | Employee
  | ProjectCustomer
  | ProjectOrganization
  | ProjectInstitution

export type MappedAddressableEntity =
  | Commune
  | YouthWelfareOffice
  | School
  | Custodian
  | Employee
  | ProjectCustomer
  | ProjectOrganization
  | ProjectInstitution
  | ContactPerson

export type CrossEntityAddress = Address & {
  crossEntity?: MappedAddressableEntity
  entityName?: string
  remarks?: string
  addressCategories?: StrictEntityId[]
}

export type AddressTypes = Pick<
  Address,
  | "doctor"
  | "employer"
  | "volunteer"
  | "specialService"
  | "externalAddress"
  | "externalPayer"
  | "otherAddress"
>

export type BaseEntity = {
  __typename?: string
  id: string
  n?: Maybe<number> | undefined
  linkedTo?: Institution[]
  organization?: Maybe<ProjectOrganization | Organization>
  institution?: Maybe<ProjectInstitution | Institution>
}

export type ColumnBodyProps = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  data: any // Row data.
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  node: any // Row node data.
  column: ColumnNode // Column node.
  field: string // Column field.
  index: number // Row index.
  frozenRow: boolean // Whether the row is frozen.
  editorInitCallback: (event: Event) => void // Editor init callback function
  rowTogglerCallback: (event: Event) => void // Row toggler callback unction
}

export const FunctionTypes = {
  B: i18n.global.t("function_types.B"),
  F: i18n.global.t("function_types.F"),
  W: i18n.global.t("function_types.W"),
}

export type FunctionTypeKey = keyof typeof FunctionTypes

export type MappedContactPerson = {
  id: string
} & ContactPerson

// Necessary since vee-validate does not seem to handle array fields very well
export type AdditionalAddressForm = {
  additionalAddress: {
    0: string
    1: string
  }
}

export const filteredAbsenceAppointments = (): (keyof typeof AppointmentType)[] => {
  const filteredKeys = Object.keys(AppointmentType).filter(
    (key) => !["Y", "Z"].includes(key)
  ) as (keyof typeof AppointmentType)[]

  return filteredKeys.sort((a, b) => {
    const descriptionA = AppointmentTypeLocale[a]
    const descriptionB = AppointmentTypeLocale[b]
    return descriptionA.localeCompare(descriptionB, "de")
  })
}

export const AppointmentTypeLocale = {
  A: i18n.global.t("personal.termine.page.types.A"), // Seminartag
  B: i18n.global.t("personal.termine.page.types.B"), // Beschäftigungsverbot
  C: i18n.global.t("personal.termine.page.types.C"), // Fachtagung
  E: i18n.global.t("personal.termine.page.types.E"), // Erkrankung Kind
  F: i18n.global.t("personal.termine.page.types.F"), // Fortbildung
  G: i18n.global.t("personal.termine.page.types.G"), // Freistellung
  K: i18n.global.t("personal.termine.page.types.K"), // Krank
  L: i18n.global.t("personal.termine.page.types.L"), // Gleittag
  M: i18n.global.t("personal.termine.page.types.M"), // Mutterschutz
  R: i18n.global.t("personal.termine.page.types.R"), // Regenerationstag
  S: i18n.global.t("personal.termine.page.types.S"), // Sonstiger Termin
  U: i18n.global.t("personal.termine.page.types.U"), // Urlaub
  W: i18n.global.t("personal.termine.page.types.W"), // Umwandlungstag
  Y: i18n.global.t("personal.termine.page.types.Y"), // Schließzeit Teamfortbildung
  Z: i18n.global.t("personal.termine.page.types.Z"), // Schließzeit
}
export const appointmentTypeLocaleValues = [
  i18n.global.t("personal.termine.page.types.A"), // Seminartag
  i18n.global.t("personal.termine.page.types.B"), // Beschäftigungsverbot
  i18n.global.t("personal.termine.page.types.C"), // Fachtagung
  i18n.global.t("personal.termine.page.types.E"), // Erkrankung Kind
  i18n.global.t("personal.termine.page.types.F"), // Fortbildung
  i18n.global.t("personal.termine.page.types.G"), // Freistellung
  i18n.global.t("personal.termine.page.types.K"), // Krank
  i18n.global.t("personal.termine.page.types.L"), // Gleittag
  i18n.global.t("personal.termine.page.types.M"), // Mutterschutz
  i18n.global.t("personal.termine.page.types.R"), // Regenerationstag
  i18n.global.t("personal.termine.page.types.S"), // Sonstiger Termin
  i18n.global.t("personal.termine.page.types.U"), // Urlaub
  i18n.global.t("personal.termine.page.types.W"), // Umwandlungstag
  i18n.global.t("personal.termine.page.types.Y"), // Schließzeit Teamfortbildung
  i18n.global.t("personal.termine.page.types.Z"), // Schließzeit
]

export type AppointmentTypeKey = keyof typeof AppointmentTypeLocale
export type CalendarEventEntry<T> = {
  type: T
  id: string
  absence?: boolean
  __typename?: string
  policy?: string
}

export type CalendarEvent<T> = {
  [key: string]: CalendarEventEntry<T>[]
}
export type MappedHolidays = {
  [key: string]: Partial<Holiday>
}
export type Closingtime = { type: AppointmentType; id: string }

export type ClosingTimes = {
  [key: string]: Closingtime
}
export type BankWithId = Bank & { id: number }

//Contracted | Open | Queued | Rejected | Reserved | Terminated // Expired?
export enum ContractStatus {
  Open = "open",
  Contracted = "contracted",
  Queued = "queued",
  Rejected = "rejected",
  Reserved = "reserved",
  Terminated = "terminated",
  //
  Expired = "expired",
}

export enum ConfirmedContractStatus {
  ByInstitution = "byInstitution",
  ByParents = "byParents",
  None = "none",
}

export enum ContractedContractStatus {
  Created = "Created",
  Signed = "Signed",
  None = "none",
}

export const CREATE_CONTRACT_POSSIBLE_STATES: { [key in ContractStatus]: ContractStatus[] } = {
  [ContractStatus.Open]: [
    ContractStatus.Queued,
    ContractStatus.Reserved,
    ContractStatus.Contracted,
  ],
  [ContractStatus.Queued]: [
    ContractStatus.Open,
    ContractStatus.Reserved,
    ContractStatus.Contracted,
    ContractStatus.Rejected,
  ],
  [ContractStatus.Reserved]: [
    ContractStatus.Open,
    ContractStatus.Queued,
    ContractStatus.Contracted,
    ContractStatus.Rejected,
  ],
  [ContractStatus.Contracted]: [
    ContractStatus.Reserved,
    ContractStatus.Rejected /*, ContractStatus.Terminated*/,
  ],
  [ContractStatus.Rejected]: [ContractStatus.Reserved, ContractStatus.Contracted],
  [ContractStatus.Terminated]: [],
  [ContractStatus.Expired]: [],
}

export const EDIT_CONTRACT_POSSIBLE_STATES: { [key in ContractStatus]: ContractStatus[] } = {
  [ContractStatus.Open]: [
    ContractStatus.Queued,
    ContractStatus.Reserved,
    ContractStatus.Contracted,
    ContractStatus.Rejected,
  ],
  [ContractStatus.Queued]: [
    ContractStatus.Open,
    ContractStatus.Reserved,
    ContractStatus.Contracted,
    ContractStatus.Rejected,
  ],
  [ContractStatus.Reserved]: [
    ContractStatus.Open,
    ContractStatus.Queued,
    ContractStatus.Contracted,
    ContractStatus.Rejected,
  ],
  [ContractStatus.Contracted]: [
    ContractStatus.Reserved,
    ContractStatus.Rejected,
    ContractStatus.Terminated,
  ],
  [ContractStatus.Rejected]: [ContractStatus.Reserved, ContractStatus.Contracted],
  [ContractStatus.Terminated]: [],
  [ContractStatus.Expired]: [],
}

export type ContractInfo = { status: ContractStatus; data: ContractStateInput }

export enum BookingTypeAbbreviation {
  /** "Alternativbuchung-1" */
  Alternative_1 = "A",
  /** "Alternativbuchung-2" */
  Alternative_2 = "B",
  /** "Kurzzeitbuchung" */
  SHORT_TIME = "K",
  /** "Standard-/Regelbuchung" */
  STANDARD = "",
  /** "Ferienbuchung-1" */
  VACATION_1 = "F",
  /** "Ferienbuchung-2" */
  VACATION_2 = "G",
  /** "Ferienbuchung-3" */
  VACATION_3 = "H",
}
export const BookingTypeLocale = {
  ALTERNATIVE_1: i18n.global.t("kinder.kinder.view.reservation.types.ALTERNATIVE_1"), // Alternativbuchung-1
  ALTERNATIVE_2: i18n.global.t("kinder.kinder.view.reservation.types.ALTERNATIVE_2"), // Alternativbuchung-2
  SHORT_TIME: i18n.global.t("kinder.kinder.view.reservation.types.SHORT_TIME"), // Kurzzeitbuchung
  STANDARD: i18n.global.t("kinder.kinder.view.reservation.types_with_abbr.STANDARD"), // Standard-/Regelbuchung
  VACATION_1: i18n.global.t("kinder.kinder.view.reservation.types.VACATION_1"), // Ferienbuchung-1
  VACATION_2: i18n.global.t("kinder.kinder.view.reservation.types.VACATION_2"), // Ferienbuchung-2
  VACATION_3: i18n.global.t("kinder.kinder.view.reservation.types.VACATION_3"), // Ferienbuchung-3
}

export type SelectedCustodianChild = {
  id: string
  child: {
    id: string
    person: {
      firstname?: string
      lastname?: string
    }
    birth: {
      date: string
    }
  }
}

const keysToIgnoreFromEntity = ["institution", "commune", "organization"] as const

type keysToIgnoreFromEntity = (typeof keysToIgnoreFromEntity)[number]

export type EntityWithoutRelations<T, ExtraKeys extends keyof T = never> = Omit<
  T,
  keysToIgnoreFromEntity | ExtraKeys
>

export type Override<T, R> = Omit<T, keyof R> & R

export type ActionRoles = { create: Permission[]; delete: Permission[] }

export enum KibigKey {
  INSTITUTION = "EINRICHTUNG",
  USER = "BENUTZER",
}

export type KibigValidationFlags = {
  hasEmpty: boolean
  hasDuplicate: boolean
  hasGeneratedDuplicate: boolean
  hasGeneratedEmpty: boolean
}

export type KibigValidationState = {
  employee: KibigValidationFlags
  child: KibigValidationFlags
}
